import { takeEvery, put } from "redux-saga/effects";
import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import { notifyError, notifySuccess } from "../../../effects-bridge";
import {
  getAllOtherNotifications,
  getOtherNotifications,
  markAsReadNotification,
  markAsReadNotificationSuccess,
  markAllAsRead,
  markAllAsReadSuccess,
  acceptApproveOrder,
  acceptApproveOrderSuccess,
  rejectApproveOrder,
  rejectApproveOrderSuccess,
  viewOrderBill,
  viewOrderBillSuccess,
  viewPurchaseOrder,
  viewPurchaseOrderSuccess,
  viewFeedback,
  viewFeedbackSuccess,
  getAllNewOnlineOrders,
  getAllNewOnlineOrdersSuccess,
  getAllNewOnlinePollingOrdersSuccess,
  testNotification,
  postActivityLog,
} from "../../slices/NotificationsSlice";

export * from "./shared";   // rule 4: the folder always has all three files

/**
 * WEB half of `redux-store/saga/notifications`.
 *
 * 🔴 `shared.ts` holds no code — nothing here is common. Every worker differs, because web
 * calls `ToastNotifyError("error:- ", error)` in seven catch blocks where **mobile has the exact
 * same lines COMMENTED OUT** in its app source. Sharing any of them would make mobile start
 * showing error toasts it has never shown.
 *
 * Beyond the toasts, web additionally has:
 *   - a `callback` + `action.payload.data ? … : action.payload` in `getAllOtherNotificationsFunc`
 *   - the whole `autoRefresh` / `skipGlobalLoader` polling branch in `getAllNewOnlineOrdersFunc`
 *   - a `traceId` SUCCESS toast in `callTestNotification`, and it sends no payload to the endpoint
 *   - a web-only `postActivityLogFunc` worker (mobile's `root` registers 10 watchers, web's 11)
 *
 * ⚠️ Every core import names the VARIANT, not the folder (hazard 6/16). Toasts go through the
 * effects bridge — UI cannot come into core.
 *
 * ⚠️ The app writes `ToastNotifyError("error:- ", error)` — TWO arguments — but web's
 * `ToastNotifyError` is declared `(message: string)` and JS discards the extra argument, so the
 * toast has only ever read "error:- ". `notifyError("error:- ")` is therefore behaviourally
 * identical, not a truncation. Verified against
 * `apps/web/src/commonComponents/ToastNotify/index.tsx:53`. The `error` is still logged.
 */
function* getAllOtherNotificationsFunc(action: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.NOTIFICATIONS.getAllOtherNotifications(),
      data: action.payload.data ? action.payload.data : action.payload,
    });
    if (action.payload.callback) {
      yield action.payload.callback(response?.results);
    }
    yield put(getOtherNotifications(response));
  } catch (error) {
    console.log("error", error);
  }
}

function* markAsReadNotificationFunc(action: any): Generator<any, void, any> {
  try {
    yield api.request({
      method: "POST",
      url: ENDPOINT.NOTIFICATIONS.markAsReadNotification(),
      data: action.payload,
    });
    yield put(markAsReadNotificationSuccess(action.payload));
  } catch (error) {
    notifyError("error:- ");
    console.log("error", error);
  }
}

function* markAllAsReadFunc(action: any): Generator<any, void, any> {
  try {
    yield api.request({
      method: "PUT",
      url: ENDPOINT.NOTIFICATIONS.markAllAsRead(),
    });
    yield put(markAllAsReadSuccess());
  } catch (error) {
    notifyError("error:- ");
    console.log("error", error);
  }
}

function* acceptApproveOrderFunc(action: any): Generator<any, void, any> {
  try {
    yield api.request({
      method: "POST",
      url: ENDPOINT.NOTIFICATIONS.acceptApproveOrder(action.payload.messageId),
      data: action.payload,
    });
    yield put(acceptApproveOrderSuccess(action.payload));
  } catch (error) {
    notifyError("error:- ");

    console.log("error", error);
  }
}

function* rejectApproveOrderFunc(action: any): Generator<any, void, any> {
  try {
    yield api.request({
      method: "POST",
      url: ENDPOINT.NOTIFICATIONS.rejectApproveOrder(action.payload.messageId),
      data: action.payload,
    });
    yield put(rejectApproveOrderSuccess(action.payload));
  } catch (error) {
    notifyError("error:- ");

    console.log("error", error);
  }
}

function* viewOrderBillFunc(action: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "GET",
      url: ENDPOINT.NOTIFICATIONS.viewOrderBill(action.payload.messageId),
    });
    yield put(viewOrderBillSuccess(response));
  } catch (error) {
    notifyError("error:- ");

    console.log("error", error);
  }
}

function* viewPurchaseOrderFunc(action: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "GET",
      url: ENDPOINT.NOTIFICATIONS.viewPurchaseOrder(action.payload.messageId),
    });
    yield put(viewPurchaseOrderSuccess(response));
  } catch (error) {
    console.log("error", error);
    notifyError("error:- ");
  }
}

function* viewFeedbackFunc(action: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "GET",
      url: ENDPOINT.NOTIFICATIONS.viewFeedback(action.payload.messageId),
    });
    yield put(viewFeedbackSuccess(response));
  } catch (error) {
    console.log("error", error);
    notifyError("error:- ");
  }
}

function* getAllNewOnlineOrdersFunc(action: any): Generator<any, void, any> {
  try {
    let url = ENDPOINT.NOTIFICATIONS.getAllNewOnlineOrders();
    if (action.payload?.autoRefresh) {
      url = url + `?autoRefresh=true`;
    }
    const response = yield api.request({
      method: "GET",
      url: url,
      skipGlobalLoader: action.payload?.skipGlobalLoader,
    } as any);
    if (response?.code === "REST_ERR_INVALID_INPUT") {
      // do nothing
    } else if (action?.payload?.autoRefresh) {
      yield put(getAllNewOnlinePollingOrdersSuccess(response?.code ? [] : response || []));
    } else {
      yield put(getAllNewOnlineOrdersSuccess(response?.code ? [] : response || []));
    }
  } catch (error) {
    notifyError("error:- ");
    console.log("error", error);
  }
}

function* callTestNotification(action: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.NOTIFICATIONS.testNotification(),
    });
    if (response) {
      if (response?.errorCode) {
        notifyError(response.errorCode);
      } else if (response?.traceId) {
        notifySuccess(`TraceId : ${response.traceId}`);
      }
    }
  } catch (error) {
    notifyError(error as any);
    console.log("error in callTestNotification", error);
  }
}

/** WEB ONLY — mobile has no activity log worker. */
function* postActivityLogFunc(action: any): Generator<any, void, any> {
  try {
    yield api.request({
      method: "POST",
      url: ENDPOINT.NOTIFICATIONS.notificationLog(),
      data: action.payload,
    });
  } catch (error) {
    console.log("error posting log", error);
    notifyError("Failed to log activity");
  }
}

export default function* root() {
  yield takeEvery(getAllOtherNotifications.type, getAllOtherNotificationsFunc);
  yield takeEvery(markAsReadNotification.type, markAsReadNotificationFunc);
  yield takeEvery(markAllAsRead.type, markAllAsReadFunc);
  yield takeEvery(acceptApproveOrder.type, acceptApproveOrderFunc);
  yield takeEvery(rejectApproveOrder.type, rejectApproveOrderFunc);
  yield takeEvery(viewOrderBill.type, viewOrderBillFunc);
  yield takeEvery(viewPurchaseOrder.type, viewPurchaseOrderFunc);
  yield takeEvery(viewFeedback.type, viewFeedbackFunc);
  yield takeEvery(getAllNewOnlineOrders.type, getAllNewOnlineOrdersFunc);
  yield takeEvery(testNotification.type, callTestNotification);
  yield takeEvery(postActivityLog.type, postActivityLogFunc);
}
